'use client';

import { Archive, ArrowDown, ShieldCheck } from 'lucide-react';
import { useTranslation } from '@/i18n/useTranslation';

const PRESERVATION_ITEMS = [
  'landing_two_ledgers.preservation_item_1',
  'landing_two_ledgers.preservation_item_2',
  'landing_two_ledgers.preservation_item_3',
  'landing_two_ledgers.preservation_item_4',
  'landing_two_ledgers.preservation_item_5',
] as const;

const TRUST_ITEMS = [
  'landing_two_ledgers.trust_item_1',
  'landing_two_ledgers.trust_item_2',
  'landing_two_ledgers.trust_item_3',
  'landing_two_ledgers.trust_item_4',
  'landing_two_ledgers.trust_item_5',
] as const;

export default function TwoLedgersSection() {
  const { t } = useTranslation();

  return (
    <section
      className="relative overflow-hidden pb-24 pt-4 lg:pb-32"
      style={{ background: 'linear-gradient(180deg, #0c1512 0%, #0e1c22 55%, #0a1614 100%)' }}
    >
      <div className="relative mx-auto max-w-6xl px-6 lg:px-10">
        <div className="grid gap-6 lg:grid-cols-2 lg:gap-8">
          <article
            className="rounded-[14px] border p-7 lg:p-8"
            style={{ background: 'rgba(255,255,255,0.035)', borderColor: 'rgba(152,242,211,0.16)' }}
          >
            <div
              className="mb-5 inline-flex h-11 w-11 items-center justify-center rounded-[9px]"
              style={{
                background: 'rgba(29,158,117,0.14)',
                border: '1px solid rgba(29,158,117,0.3)',
              }}
            >
              <Archive size={20} style={{ color: '#98f2d3' }} aria-hidden="true" />
            </div>
            <h3 className="font-serif text-2xl leading-tight text-[#f4efe4]">
              Preserve the human meaning.
            </h3>
            <p className="mt-3 text-sm leading-6 text-[#9db4aa] sm:text-[15px] sm:leading-7">
              {t('landing_two_ledgers.preservation_body' as any)}
            </p>
            <ul
              className="mt-5 space-y-2.5 border-t pt-5"
              style={{ borderColor: 'rgba(152,242,211,0.12)' }}
            >
              {PRESERVATION_ITEMS.map((key) => (
                <li key={key} className="flex items-start gap-2 text-sm text-[#c7d6cf]">
                  <span
                    className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full"
                    style={{ background: '#1d9e75' }}
                    aria-hidden="true"
                  />
                  {t(key as any)}
                </li>
              ))}
            </ul>
          </article>

          <article
            className="rounded-[14px] border p-7 lg:p-8"
            style={{ background: 'rgba(255,255,255,0.035)', borderColor: 'rgba(217,164,65,0.18)' }}
          >
            <div
              className="mb-5 inline-flex h-11 w-11 items-center justify-center rounded-[9px]"
              style={{
                background: 'rgba(217,164,65,0.14)',
                border: '1px solid rgba(217,164,65,0.32)',
              }}
            >
              <ShieldCheck size={20} style={{ color: '#e9c27a' }} aria-hidden="true" />
            </div>
            <h3 className="font-serif text-2xl leading-tight text-[#f4efe4]">
              Support what must be trusted.
            </h3>
            <p className="mt-3 text-sm leading-6 text-[#9db4aa] sm:text-[15px] sm:leading-7">
              {t('landing_two_ledgers.trust_body' as any)}
            </p>
            <ul
              className="mt-5 space-y-2.5 border-t pt-5"
              style={{ borderColor: 'rgba(217,164,65,0.14)' }}
            >
              {TRUST_ITEMS.map((key) => (
                <li key={key} className="flex items-start gap-2 text-sm text-[#c7d6cf]">
                  <span
                    className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full"
                    style={{ background: '#d9a441' }}
                    aria-hidden="true"
                  />
                  {t(key as any)}
                </li>
              ))}
            </ul>
          </article>
        </div>

        <div className="mx-auto mt-8 flex max-w-lg flex-col items-center gap-2 text-center">
          <ArrowDown size={16} style={{ color: 'rgba(244,239,228,0.35)' }} aria-hidden="true" />
          <p className="text-sm font-medium leading-6 text-[#c7d6cf]">
            One layer protects meaning. The other supports credibility. Together, they create a
            fuller human record.
          </p>
        </div>
      </div>
    </section>
  );
}
